import React, { useEffect, useState } from 'react';
import api from '../services/ApiService';
import liveSocket from '../services/LiveSocketService';
import { ZoomTable, ZoomBadge } from './zoom/ZoomTable';

type AgentEvent = {
  id: number;
  tenant_id: number;
  user_id: number;
  username: string;
  previous_state: string;
  new_state: string;
  pause_reason?: string;
  queue_name?: string;
  created_at: string;
};

type LiveAgent = {
  id: number;
  username: string;
  tenant_id: number;
  role?: string;
  user_type?: string;
  cc_enabled?: number;
  queue_enabled?: number;
  supervisor_enabled?: number;
  is_active?: number;
  state: string;
  queue_name?: string;
  updated_at?: string;
};

export default function SupervisorMonitor({ zoom }: { zoom?: boolean }) {
  const [events, setEvents] = useState<AgentEvent[]>([]);
  const [agents, setAgents] = useState<LiveAgent[]>([]);
  const [loading, setLoading] = useState(true);
  const [monSearch, setMonSearch] = useState('');

  const loadData = async () => {
    try {
      const [eventData, liveData] = await Promise.all([
        api.getAgentEvents(),
        api.getLiveAgents()
      ]);

      setEvents(eventData || []);
      setAgents(liveData || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
  loadData();

  liveSocket.connect();

  const unsubscribe = liveSocket.subscribe((event: any) => {
    if (
      [
        'agent_state_changed',
        'queue_login',
        'queue_logout'
      ].includes(event.type)
    ) {
      loadData();
    }
  });

  return () => {
    unsubscribe();
  };
}, []);

  const totals = {
    total: agents.length,
    available: agents.filter((a) => a.state === 'available').length,
    paused: agents.filter((a) => a.state === 'paused').length,
    talking: agents.filter((a) => a.state === 'talking').length,
    offline: agents.filter((a) => a.state === 'offline').length
  };

  // ── Zoom-console variant (TEC tenant): summary + current status only ──────────
  if (zoom) {
    const fmt = (t?: string) => {
      if (!t) return '—';
      const d = new Date(t);
      if (isNaN(+d)) return t;
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) + ' · ' + d.toLocaleDateString();
    };
    const STATE_LABEL: Record<string, string> = { available: 'Ready', talking: 'On Call', paused: 'Break', offline: 'Offline', wrapup: 'Wrap-up', ringing: 'Ringing' };
    const STATE_TONE: Record<string, any> = { available: 'green', talking: 'blue', paused: 'amber', offline: 'gray', wrapup: 'amber', ringing: 'blue' };
    const cards = [
      { label: 'Total Agents', value: totals.total, tone: 'blue' },
      { label: 'Ready', value: totals.available, tone: 'green' },
      { label: 'On Call', value: totals.talking, tone: 'blue' },
      { label: 'On Break', value: totals.paused, tone: 'amber' },
      { label: 'Offline', value: totals.offline, tone: 'gray' },
    ];
    const rows = (agents || []).filter((a) =>
      !monSearch.trim() || `${a.username} ${a.queue_name || ''} ${a.role || ''}`.toLowerCase().includes(monSearch.toLowerCase()));

    const columns: any[] = [
      { key: 'agent', label: 'Agent', render: (a: LiveAgent) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span className="z-mon-dot" data-state={a.state || 'offline'} />
          <span className="z-tbl-primary-cell">{a.username}</span>
        </div>
      ) },
      { key: 'state', label: 'Status', render: (a: LiveAgent) => <ZoomBadge text={STATE_LABEL[a.state] || a.state || 'Offline'} tone={STATE_TONE[a.state] || 'gray'} /> },
      { key: 'queue', label: 'Queue', render: (a: LiveAgent) => a.queue_name || '—' },
      { key: 'role', label: 'Role', render: (a: LiveAgent) => a.role || '—' },
      { key: 'updated', label: 'Last Update', render: (a: LiveAgent) => <span style={{ color: '#6B7280' }}>{fmt(a.updated_at)}</span> },
    ];

    return (
      <div className="z-tbl-page">
        <div className="z-tbl-titlebar">
          <div>
            <h1 className="z-tbl-title">Supervisor Monitor</h1>
            <div className="z-tbl-subtitle">Live agent presence — current status at a glance.</div>
          </div>
        </div>

        <div className="z-mon-cards">
          {cards.map((c) => (
            <div key={c.label} className="z-mon-card" data-tone={c.tone}>
              <div className="z-mon-value">{c.value}</div>
              <div className="z-mon-label">{c.label}</div>
            </div>
          ))}
        </div>

        {loading ? (
          <div className="z-tbl-empty"><div>Loading live agents…</div></div>
        ) : (
          <ZoomTable
            title=""
            search={monSearch}
            onSearch={setMonSearch}
            searchPlaceholder="Search agents"
            actions={<button className="z-btn" onClick={loadData}>↻ Refresh</button>}
            columns={columns}
            rows={rows}
            rowKey={(a: LiveAgent) => a.id}
            selectable={false}
            emptyText="No agents online"
            initialPageSize={25}
          />
        )}
      </div>
    );
  }

  return (
    <>
      <div className="admin-page-title">
        Supervisor Monitor
      </div>

      <div className="admin-page-subtitle">
        Live tenant-safe agent presence and activity timeline.
      </div>

      <div className="dashboard-grid">
        <StatCard title="Total Agents" value={totals.total} />
        <StatCard title="Available" value={totals.available} />
        <StatCard title="Paused" value={totals.paused} />
        <StatCard title="Talking" value={totals.talking} />
        <StatCard title="Offline" value={totals.offline} />
      </div>

      <div className="content-card">
        <div className="admin-card-header">
          <h3>Live Agents</h3>

          <button
            className="secondary-btn"
            onClick={loadData}
          >
            Refresh
          </button>
        </div>

        {loading && (
          <p className="admin-muted">
            Loading live agents...
          </p>
        )}

        {!loading && agents.length === 0 && (
          <p className="admin-muted">
            No CC agents found for this tenant.
          </p>
        )}

        {agents.length > 0 && (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Agent</th>
                <th>State</th>
                <th>Queue</th>
                <th>Role</th>
                <th>Last Update</th>
              </tr>
            </thead>

            <tbody>
              {agents.map((a) => (
                <tr key={a.id}>
                  <td>
                    <strong>{a.username}</strong>
                    <div className="admin-muted">
                      Tenant {a.tenant_id}
                    </div>
                  </td>

                  <td>
                    <span className={`admin-pill ${stateClass(a.state)}`}>
                      {a.state || 'offline'}
                    </span>
                  </td>

                  <td>{a.queue_name || '-'}</td>

                  <td>{a.role || '-'}</td>

                  <td>{a.updated_at || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="content-card">
        <div className="admin-card-header">
          <h3>Agent State Timeline</h3>
        </div>

        {events.length === 0 && (
          <p className="admin-muted">
            No agent state events found.
          </p>
        )}

        {events.length > 0 && (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Agent</th>
                <th>Previous</th>
                <th>Current</th>
                <th>Reason</th>
                <th>Queue</th>
              </tr>
            </thead>

            <tbody>
              {events.map((e) => (
                <tr key={e.id}>
                  <td>{e.created_at}</td>

                  <td>
                    <strong>{e.username}</strong>
                    <div className="admin-muted">
                      Tenant {e.tenant_id}
                    </div>
                  </td>

                  <td>
                    <span className="admin-pill">
                      {e.previous_state || 'offline'}
                    </span>
                  </td>

                  <td>
                    <span className={`admin-pill ${stateClass(e.new_state)}`}>
                      {e.new_state}
                    </span>
                  </td>

                  <td>{e.pause_reason || '-'}</td>

                  <td>{e.queue_name || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

function StatCard({ title, value }: any) {
  return (
    <div className="stat-card">
      <div className="stat-title">{title}</div>
      <div className="stat-value">{value}</div>
    </div>
  );
}

function stateClass(state: string) {
  if (state === 'available') return 'green';
  if (state === 'talking') return 'green';
  if (state === 'paused') return 'red';
  if (state === 'offline') return 'red';
  if (state === 'ringing') return '';
  if (state === 'wrapup') return '';
  return '';
}